import React, { useEffect, useState } from "react";
import axios from "axios";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { toast } from "react-toastify";
import { useCustomers } from "../../context/CustomerContext";

const SyncCustomer = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const { fetchCustomers } = useCustomers();

    const [syncCustomers, setSyncCustomers] = useState([]);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const [loading, setLoading] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);

    const [system, setSystem] = useState("reception");

    const receptionUrl = process.env.REACT_APP_RECEPTION_URL;
    const conferenceUrl = process.env.REACT_APP_CONFERENCE_URL;
    const conferenceToken = process.env.REACT_APP_CONFERENCE_TOKEN;

    const activeBaseUrl = system === "reception" ? receptionUrl : conferenceUrl;
    const activeToken =
        system === "reception"
            ? localStorage.getItem("token")
            : conferenceToken;

    // Fetch customers for a page
    const fetchSyncCustomers = async (p = 1) => {
        try {
            setLoading(true);

            const res = await axios.post(`${activeBaseUrl}/getCustomersPaginated`, {
                token: activeToken,
                page: p,
                limit: 100,
            });

            setSyncCustomers(res.data.customers || []);
            setTotalPages(res.data.pagination?.total_pages || 1);
        } catch (err) {
            console.error("Error fetching customers:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSyncCustomers(page);
    }, [page, system]);

    // SYNC BUTTON
    const handleSync = async () => {
        try {
            setSyncLoading(true);

            const payload = syncCustomers.map((c) => ({
                name: c.Name,
                email: c.Email || "",
                phone: c.Phone || "",
                address: c.Address || "",
                status: true,
                source: system === "reception" ? "Alice Receptions" : "Alice Conference",
            }));

            await axios.post(`${baseUrl}/add-customers`, payload);

            const syncStatusPayload = {
                token: activeToken,
                customers: syncCustomers.map((c) => ({
                    Phone: c.Phone,
                    Name: c.Name,
                })),
            };

            await axios.post(`${activeBaseUrl}/updateCustomerSyncStatus`, syncStatusPayload);

            toast.success("Customers synced & status updated successfully!");
            fetchSyncCustomers();
            fetchCustomers();
        } catch (err) {
            console.error(err);
            toast.error("Failed to sync customers.");
        } finally {
            setSyncLoading(false);
        }
    };

    return (
        <div className="p-5 bg-white shadow rounded-lg">
            {/* Header Row */}
            <div className="flex items-center justify-between mb-2">

                <h2 className="text-lg font-semibold">Customer List</h2>

                <div className="flex items-center gap-3">
                    <select
                        value={system}
                        onChange={(e) => {
                            setSystem(e.target.value);
                            setPage(1);
                        }}
                        className="px-3 py-2 border rounded text-sm"
                    >
                        <option value="reception">Alice Receptions</option>
                        <option value="conference">Alice Conference</option>
                    </select>

                    <button
                        onClick={handleSync}
                        disabled={syncLoading}
                        className="px-3 py-2 font-semibold rounded hover:shadow-md border text-sm bg-white hover:bg-gray-100 text-green-500 hover:text-green-600 disabled:opacity-50"
                    >
                        {syncLoading ? (
                            <>
                                <AiOutlineLoading3Quarters className="animate-spin" />
                                Syncing...
                            </>
                        ) : (
                            "Sync Data"
                        )}
                    </button>
                </div>

            </div>

            {/* Loading State */}
            {loading && (
                <div className="flex justify-center items-center py-8">
                    <AiOutlineLoading3Quarters className="animate-spin text-3xl text-indigo-600" />
                </div>
            )}

            {!loading && (
                <>
                    {/* Table */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full bg-white border rounded-lg shadow-sm text-sm">
                            <thead className="bg-gray-100 text-center">
                                <tr>
                                    <th className="px-3 py-2 border">ID</th>
                                    <th className="px-3 py-2 border">Name</th>
                                    <th className="px-3 py-2 border">Email</th>
                                    <th className="px-3 py-2 border">Phone</th>
                                    <th className="px-3 py-2 border">Address</th>
                                </tr>
                            </thead>

                            <tbody>
                                {syncCustomers.map((c) => (
                                    <tr key={c.id} className="text-center">
                                        <td className="px-3 py-2 border">{c.id}</td>
                                        <td className="px-3 py-2 border">{c.Name}</td>
                                        <td className="px-3 py-2 border">{c.Email || "N/A"}</td>
                                        <td className="px-3 py-2 border">{c.Phone || "N/A"}</td>
                                        <td className="px-3 py-2 border">{c.Address || "N/A"}</td>
                                    </tr>
                                ))}

                                {syncCustomers.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="p-4 text-gray-500 font-semibold text-center">
                                            No matching customers.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-center items-center gapx-3 py-2 mt-4">
                        <button
                            disabled={page <= 1}
                            onClick={() => setPage((p) => p - 1)}
                            className="px-3 py-1 rounded border text-sm disabled:opacity-50 hover:bg-indigo-100"
                        >
                            Prev
                        </button>

                        {[...Array(totalPages)].map((_, i) => {
                            const num = i + 1;
                            return (
                                <button
                                    key={num}
                                    onClick={() => setPage(num)}
                                    className={`px-3 py-1 rounded text-sm border transition 
                                        ${page === num
                                            ? "bg-indigo-600 text-white border-indigo-600"
                                            : "hover:bg-indigo-100"
                                        }`}
                                >
                                    {num}
                                </button>
                            );
                        })}

                        <button
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => p + 1)}
                            className="px-3 py-1 rounded border text-sm disabled:opacity-50 hover:bg-indigo-100"
                        >
                            Next
                        </button>
                    </div>
                </>
            )}

        </div>
    );
};

export default SyncCustomer;
